namespace Solovis.UI {

    export interface StringFilterListControlOptions extends Solovis.UI.BaseControlOptions {
        listItems: any[];
        listFilter?: any;
        itemDivGenerator?: any;
        useDefaultSort?: boolean;
        selectedItem?: any;
        additionalListItem?: any;
        additionalListItemDivGenerator?: any;
        filterInputValue?: string;
    }

    /**
     * StringFilterListControl is a UI element for hosting a list of items that is filterable by a string input.
     * 
     * options:
     * 
     * itemDivGenerator: function to create the container for the selected item.  default just creates an empty div containing the label of the item.
     *                   Parameter is an itemHolder object.  ItemHolder must contain an item object.
     * listFilter: filter function for the list.  format is function(itemList, filterOptions).  Should return a (sorted) list of itemHolders
     * 
     * listItems: array of objects     
     * 
     * useDefaultSort: whether or not to sort the items by the label property with prefixes taking precedence
     * 
     * selectedItem: initially selected item in the list
     */
    export class StringFilterListControl extends Solovis.UI.BaseControl {

        public _listItems: any[];
        public _listFilter: any;
        public _itemDivGenerator: any;
        public _useDefaultSort: any;
        public _selectedItem;
        public _additionalListItem: any;
        public _additionalListItemDivGenerator: any;

        public _dockPanelControl: Solovis.UI.DockPanelControl;
        public _filterInput: Solovis.UI.StringFilterListControl.FilterInput;
        public _filterChangedEventHandler: any;
        public _upDownArrowKeyEventHandler: any;
        public _listDisplay: Solovis.UI.StringFilterListControl.ListDisplay;
        public _enterKeyEventHandler: any;
        public _filterInputValue: any;        

        constructor(options: StringFilterListControlOptions) {
            super(options);

            var self = this;

            if (!options.listItems) {
                throw "Missing required parameter: listItems.";
            }
            this._listItems = options.listItems;

            this._listFilter = options.listFilter ? options.listFilter : null;
            this._itemDivGenerator = options.itemDivGenerator ? options.itemDivGenerator : null;
            this._useDefaultSort = options.useDefaultSort ? options.useDefaultSort : null;
            this._selectedItem = options.selectedItem ? options.selectedItem : null;
            this._additionalListItem = options.additionalListItem || null;
            this._additionalListItemDivGenerator = options.additionalListItemDivGenerator || null;    
            this._filterInputValue = options.filterInputValue || null;        
        }

        public static create(options: StringFilterListControlOptions) {
            return new Solovis.UI.StringFilterListControl(options);
        }

        public doRender() {
            var self = this;            
            this.container.addClass("solovis-input");

            this._dockPanelControl = new Solovis.UI.DockPanelControl({
                controlHost: this.container
            });

            this._dockPanelControl.setAsCurrentControl();

            this._filterInput = new Solovis.UI.StringFilterListControl.FilterInput({});            

            this._filterChangedEventHandler = _.debounce(function () { self._listDisplay.filter(); }, 150);

            this._upDownArrowKeyEventHandler = function (e, keyData) {
                if (self._listDisplay) {
                    var currentSelectedItem = self._listDisplay.value();
                    var currentSelectedItemIndex, prevItemIndex, nextItemIndex;
                    if (currentSelectedItem && self._listDisplay._displayedItemHolders.length > 1) {
                        currentSelectedItemIndex = self._listDisplay._displayedItemIndex(currentSelectedItem);

                        switch (currentSelectedItemIndex) {
                            case 0:
                                prevItemIndex = self._listDisplay._displayedItemHolders.length - 1;
                                nextItemIndex = currentSelectedItemIndex + 1;
                                break;
                            case self._listDisplay._displayedItemHolders.length - 1:
                                prevItemIndex = currentSelectedItemIndex - 1;
                                nextItemIndex = 0;
                                break;
                            default:
                                prevItemIndex = currentSelectedItemIndex - 1;
                                nextItemIndex = currentSelectedItemIndex + 1;
                                break;
                        }
                    }
                    else {
                        prevItemIndex = 0;
                        nextItemIndex = 0;
                    }

                    switch (keyData.key) {
                        case 40: // down
                            if (self._listDisplay._displayedItemHolders.length) {
                                self._listDisplay._selectItemAtIndex(nextItemIndex, "down");
                            }
                            break;
                        case 38: // up
                            if (self._listDisplay._displayedItemHolders.length) {
                                self._listDisplay._selectItemAtIndex(prevItemIndex, "up");
                            }
                            break;
                    }
                }
            };

            this._enterKeyEventHandler = function () {
                var currentSelectedItem = self._listDisplay.value();
                if (currentSelectedItem) {
                    var currentSelectedItemIndex = self._listDisplay._displayedItemIndex(currentSelectedItem);
                    let itemHolder = self._listDisplay._displayedItemHolders[currentSelectedItemIndex];
                    if (itemHolder && itemHolder.itemDiv) {
                        itemHolder.itemDiv.click();
                    }
                }
            };

            $(this._filterInput).bind("valueChanged", this._filterChangedEventHandler);

            $(this._filterInput).bind("escapeKey", function () {
                $(self).trigger("escapeKey");
            });

            $(this._filterInput).bind("upDownArrowKey", this._upDownArrowKeyEventHandler);

            $(this._filterInput).bind("enterKey", this._enterKeyEventHandler);

            $(this._filterInput).bind("renderComplete", function() {
                self._filterInput.setValue(self._filterInputValue)  
            });
            
            this._listDisplay = new Solovis.UI.StringFilterListControl.ListDisplay({
                listItems: this._listItems,
                listFilter: this._listFilter,
                itemDivGenerator: this._itemDivGenerator,
                useDefaultSort: this._useDefaultSort,
                filterInput: this._filterInput,
                selectedItem: this._selectedItem,
                scrollableParentElement: this._dockPanelControl.getMainPanelControlHost().container,
                additionalListItem: this._additionalListItem,
                additionalListItemDivGenerator: this._additionalListItemDivGenerator
            });

            $(this._listDisplay).bind("valueChanged", function () { self.notifySelectionChanged() });

            this._dockPanelControl.setHeaderPanelControl(this._filterInput);
            this._dockPanelControl.setMainPanelControl(this._listDisplay);

            var mainPanelControlHost = this._dockPanelControl.getMainPanelControlHost();
            mainPanelControlHost.container
                .css("box-sizing", "border-box")
                .css("border", "1px solid #ddd");
        }        
        
        public focusFilter() {
            this._filterInput._focus();
        };

        public getAvailableHeight() : number {
            // The -2 is used to counter kendo's box-sizing: content-box in all k-widget styles
            return this._dockPanelControl.getAvailableHeight() + 2;
        }

        public setAvailableHeight(availableHeight) {
            // The -2 is used to counter kendo's box-sizing: content-box in all k-widget styles
            this._dockPanelControl.setAvailableHeight(availableHeight - 2);
        }

        public notifySelectionChanged() {
            var item = this.getSelectedItem();
            $(this).trigger("selectionChanged", { item: item });
        }

        public getSelectedItem() {
            return this._listDisplay.value();
        }

        public setListItems(items) {
            this._listDisplay._listItems = items;

            this.resetFilter();
        }

        public resetFilter() {
            this._filterInput.setValue(null);

            this._listDisplay.filter();
        }

        public scrollToSelectedItem() {
            if (this._selectedItem) {
                this._listDisplay._scrollToItem({
                    item: this._selectedItem
                });
            }
        }
    }

}


namespace Solovis.UI.StringFilterListControl {

    export class FilterInput extends Solovis.UI.BaseControl {

        public _keydownEventHandler: any;
        public _filterQueryInput: JQuery;


        constructor(options) {
            super(options);
        }

        public doRender() {
            var self = this;

            this.container
                .css("padding", "0 5px 0 5px");

            this._keydownEventHandler = function (e) {
                var key = e.keyCode || e.which;
                // Handle Escape
                if (key === 27) {
                    $(self).trigger("escapeKey");
                }
                else if (key === 38 || key === 40) {
                    $(self).trigger("upDownArrowKey", { key: key });
                }
                else if (key === 13) {
                    $(self).trigger("enterKey");
                }
                else {
                    self.notifyValueChanged();
                }
            };

            this._filterQueryInput = $("<input>")
                .css("width", "100%")
                .addClass("k-textbox")
                .appendTo(this.container);

            this._filterQueryInput.bind("keydown", this._keydownEventHandler);
        }

        public _focus() {
            this._filterQueryInput.focus();
        }

        public doDestroy() {
        }

        public notifyValueChanged() {
            $(this).trigger("valueChanged");
        }

        public getValue() {
            return { queryText: this._filterQueryInput.val() };
        }

        public setValue(value) {
            if (Solovis.isNull(value) || Solovis.isNull(value.queryText)) {
                this._filterQueryInput.val("");
            }

            else if (Solovis.isNotNull(value.queryText)) {
                this._filterQueryInput.val(value.queryText);
            }
        }
    }


    export interface ListDisplayOptions extends BaseControlOptions {
        listItems: any[];
        listFilter: any;
        itemDivGenerator: any;
        useDefaultSort: boolean;
        filterInput: any;
        selectedItem: any;
        scrollableParentElement: any;
        additionalListItem?: any;
        additionalListItemDivGenerator?: any;
    }

    export class ListDisplay extends Solovis.UI.BaseControl {

        public _listItems;
        public _displayedItemHolders;
        public _selectedItem;
        public _listFilter;
        public _itemDivGenerator;
        public _useDefaultSort;
        public _filterInput;        
        public _scrollableParentElement;
        public _listDiv: JQuery;        


        constructor(options: ListDisplayOptions) {
            super(options);

            var self = this;

            if (!options.listItems) {
                throw "Missing required parameter: listItems.";
            }
            this._listItems = options.listItems;
            this._displayedItemHolders = [];
            this._selectedItem = null;

            this._listFilter = options.listFilter ? options.listFilter : this._defaultListFilter;
            this._itemDivGenerator = options.itemDivGenerator ? options.itemDivGenerator : this._defaultItemDivGenerator;
            this._useDefaultSort = options.useDefaultSort ? options.useDefaultSort : null;
            this._filterInput = options.filterInput ? options.filterInput : null;
            this._selectedItem = options.selectedItem ? options.selectedItem : null;
            this._scrollableParentElement = options.scrollableParentElement ? options.scrollableParentElement : null;            
        }

        public doDestroy() {
        }

        public _displayedItemIndex(item) {
            var index = -1;

            var equals = function (itemA, itemB) {
                var result = true;
                for (var property in itemA) {
                    if (itemA.hasOwnProperty(property)) {
                        if (itemA[property] != itemB[property]) {
                            result = false;
                        }
                    }
                }
                return result;
            }

            if (item) {
                $(this._displayedItemHolders).each(function (i, displayedItemHolder: any) {
                    if (equals(displayedItemHolder.item, item)) {
                        index = i;
                        return false;
                    }
                });
            }

            return index;
        }

        public _selectItemAtIndex(index, direction) {
            if (Solovis.isNotNull(index) && $.isNumeric(index)) {
                var i = parseInt(index);
                var itemHolderAtIndex = this._displayedItemHolders[i];
                var itemAtIndex = itemHolderAtIndex.item;
                this._listDiv.find("div.filterlistcontrol-listitem").removeClass("active");
                itemHolderAtIndex.itemDiv.addClass("active");
                this._selectedItem = itemAtIndex;

                direction = direction.toLowerCase();

                this._scrollToItem({
                    item: this._selectedItem,
                    direction: direction,
                    checkIfVisible: true
                });
            }
        }

        public doRender() {
            var self = this;

            this.container
                .css("overflow", "hidden");

            this._listDiv = $("<div>")
                .addClass("filterlistcontrol-listbox")
                .appendTo(this.container);

            // Initially call filter() with no query to populate the list.
            this.filter();
        }

        public _populateListDiv = function (itemHolders) {
            var self = this;

            this._listDiv.html("");

            this._displayedItemHolders = [];

            var filterOptions = this._getFilterOptions();

            $(itemHolders).each(function (index, itemHolder: any) {                               
                
                const itemDiv = (self._itemDivGenerator || self._defaultItemDivGenerator)(itemHolder, filterOptions);

                itemHolder.itemDiv = itemDiv;

                self._displayedItemHolders.push(itemHolder);

                itemDiv
                    .addClass("filterlistcontrol-listitem")
                    .click(function () {
                        self._listDiv.find("div.filterlistcontrol-listitem").removeClass("active");

                        itemDiv.addClass("active");

                        self._selectedItem = itemHolder.item;

                        self.notifyValueChanged();

                    });

                self._listDiv.append(itemDiv);
            });
            
            if(self.options.additionalListItemDivGenerator && self.options.additionalListItem) {
                let itemHolder = self.options.additionalListItem;
                const itemDiv = self.options.additionalListItemDivGenerator(itemHolder, filterOptions);
                itemHolder.itemDiv = itemDiv;
                self._displayedItemHolders.push(itemHolder);    
                itemDiv
                    .addClass("filterlistcontrol-listitem")
                    .click(function () {                                                
                        self._selectedItem = itemHolder;
                        self.notifyValueChanged();                        
                    });

                self._listDiv.append(itemDiv);
            }

        }

        public filter() {
            var self = this;

            var filterResults = this._evaluateFilter(this._listFilter);

            this._populateListDiv(filterResults);

            this._scrollableParentElement.scrollTop(0);
        };

        public _scrollToItem(options) {
            var self = this;
            var direction = options.direction ? options.direction : "up";
            var checkIfVisible = options.checkIfVisible ? options.checkIfVisible : false;
            var item = options.item;

            var parentOffsetTop = $(this._scrollableParentElement).offset().top;
            var parentScrollTop = this._scrollableParentElement.scrollTop();
            var parentHeight = $(self._scrollableParentElement).height();
            var parentScrollHeight = $(self._scrollableParentElement)[0].scrollHeight;
            var parentInnerHeight = $(this._scrollableParentElement).innerHeight();


            var itemHolder = _.find(this._displayedItemHolders, function (itemHolder: any) {
                var isMatch = true;
                for (var property in item) {
                    if (item.hasOwnProperty(property)) {
                        if (itemHolder.item[property] !== item[property]) {
                            isMatch = false;
                        }
                    }
                }
                return isMatch;
            });

            var isScrolledIntoView = function (itemDiv) {
                var div = $(itemDiv);
                var divTop = div.offset().top;
                var divHeight = div.outerHeight();

                return divTop > parentOffsetTop && (divTop + divHeight) < (parentOffsetTop + parentHeight);
            }


            if (itemHolder && itemHolder.itemDiv.length) {

                var offsetTop = $(itemHolder.itemDiv).offset().top;
                var itemDivOuterHeight = itemHolder.itemDiv.outerHeight();

                var doScroll = true;


                if (checkIfVisible) {
                    var isVisible = isScrolledIntoView(itemHolder.itemDiv);

                    if (isVisible) {
                        doScroll = false;
                    }
                }

                if (doScroll) {
                    if (direction === "up") {
                        this._scrollableParentElement.scrollTop(offsetTop - parentOffsetTop + parentScrollTop);
                    }
                    else {
                        this._scrollableParentElement.scrollTop(offsetTop + parentScrollTop + itemDivOuterHeight - parentOffsetTop - parentInnerHeight);
                    }
                }
            }
        }

        public _getFilterOptions() {
            return this._filterInput.getValue();
        }

        public _defaultListSort(listItemHolders) {
            function compare(a, b) {
                if (a.isPrefix && !b.isPrefix) {
                    return -1;
                }
                if (!a.isPrefix && b.isPrefix) {
                    return 1;
                }

                if (a.labelLower < b.labelLower) {
                    return -1;
                }
                if (a.labelLower > b.labelLower) {
                    return 1;
                }
                return 0;
            }

            return listItemHolders.sort(compare);
        }

        public _evaluateFilter(filter) {

            var filterOptions = this._getFilterOptions();

            var resultItemHolderList = filter(this._listItems, filterOptions);

            if (this._useDefaultSort) {

                var sortedItemHolderList = this._defaultListSort(resultItemHolderList);

                return sortedItemHolderList;
            }
            else {
                return resultItemHolderList;
            }
        }

        public _defaultListFilter(itemList, filterOptions) {
            var resultList = [];

            var queryTextLower = filterOptions.queryText.toLowerCase();

            $(itemList).each(function (index, item: any) {
                if (!_.isUndefined(item.label)) {
                    var labelLower = item.label.toLowerCase();
                    var match = labelLower.indexOf(queryTextLower);
                    if (match !== -1) {
                        resultList.push({
                            isPrefix: match === 0,
                            item: item,
                            labelLower: labelLower
                        });
                    }
                }
            });

            return resultList;
        }

        public _defaultItemDivGenerator(itemHolder, filterOptions) {

            var itemDiv = $("<div>").text(itemHolder.item.label);

            var isSelectedItem = false;
            var selectedItem = this._selectedItem;
            if (selectedItem) {
                isSelectedItem = true;
                for (var property in selectedItem) {
                    if (selectedItem.hasOwnProperty(property)) {
                        if (selectedItem[property] !== itemHolder.item[property]) {
                            isSelectedItem = false;
                        }
                    }
                }
            }

            if (isSelectedItem) {
                itemDiv.addClass("active");
            }

            return itemDiv;
        }

        public notifyValueChanged() {
            $(this).trigger("valueChanged");
        }

        public value(value?: any): any {
            if (!arguments.length) {
                return this._getValue();
            }
            else {
                this._setValue(value);
                return null;
            }
        }

        public _getValue() {
            return this._selectedItem;
        }

        public _setValue(value) {
            this._selectedItem = value;

            this.filter();

            this.notifyValueChanged();
        }
    }

}
